import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { EditUserForm } from './EditUserForm';
import { useDeleteUser } from '@/hooks/users';
import type { User } from './types';
import axios from 'axios';

const SKELETON_ROWS = 5;

function SkeletonTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Created</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton className='h-4 w-32' /></TableCell>
            <TableCell><Skeleton className='h-4 w-48' /></TableCell>
            <TableCell><Skeleton className='h-5 w-14 rounded' /></TableCell>
            <TableCell><Skeleton className='h-4 w-24' /></TableCell>
            <TableCell />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function UsersTable({ users, isLoading }: { users: User[]; isLoading: boolean }) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deleteMutation = useDeleteUser();

  if (isLoading) return <SkeletonTable />;
  if (users.length === 0) return <p className='text-sm text-muted-foreground'>No users found.</p>;

  return (
    <>
      {deleteMutation.isError && (
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>
            {axios.isAxiosError(deleteMutation.error)
              ? (deleteMutation.error.response?.data?.error ?? 'Failed to delete user')
              : 'Failed to delete user'}
          </AlertDescription>
        </Alert>
      )}
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Created</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <>
              <TableRow key={user.id}>
                <TableCell className='font-medium'>{user.name}</TableCell>
                <TableCell className='text-muted-foreground'>{user.email}</TableCell>
                <TableCell>
                  <span
                    className='inline-flex items-center gap-1.5 text-xs font-medium font-mono-data'
                    style={
                      user.role === 'ADMIN'
                        ? { color: 'var(--status-new)' }
                        : { color: 'var(--status-open)' }
                    }
                  >
                    <span
                      className='w-1.5 h-1.5 rounded-full shrink-0'
                      style={{
                        backgroundColor: user.role === 'ADMIN' ? 'var(--status-new)' : 'var(--status-open)',
                        boxShadow: user.role === 'ADMIN' ? '0 0 5px var(--status-new)' : '0 0 5px var(--status-open)',
                      }}
                    />
                    {user.role}
                  </span>
                </TableCell>
                <TableCell className='text-muted-foreground font-mono-data text-xs'>
                  {new Date(user.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className='flex items-center gap-2 justify-end'>
                    {deletingId === user.id ? (
                      <>
                        <span className='text-xs text-muted-foreground'>Delete?</span>
                        <Button
                          size='sm'
                          variant='destructive'
                          disabled={deleteMutation.isPending}
                          onClick={() =>
                            deleteMutation.mutate(user.id, {
                              onSettled: () => setDeletingId(null),
                            })
                          }
                        >
                          Confirm
                        </Button>
                        <Button size='sm' variant='outline' onClick={() => setDeletingId(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => setEditingId(editingId === user.id ? null : user.id)}
                        >
                          Edit
                        </Button>
                        {user.role !== 'ADMIN' && (
                          <Button
                            size='sm'
                            variant='destructive'
                            onClick={() => setDeletingId(user.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              {editingId === user.id && (
                <TableRow key={`edit-${user.id}`} className='bg-muted/30 hover:bg-muted/30'>
                  <TableCell colSpan={5} className='p-4'>
                    <EditUserForm
                      user={user}
                      onSaved={() => setEditingId(null)}
                      onCancel={() => setEditingId(null)}
                    />
                  </TableCell>
                </TableRow>
              )}
            </>
          ))}
        </TableBody>
      </Table>
      </div>
    </>
  );
}
